import React, { useEffect, useState } from "react";

// react-router components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import TimeDisplay from "../../../components/TimeDisplay";
// import SoftInput from "../../../components/SoftInput";

import pageHeadings from "../../../constants/pageHeadings";

import Breadcrumbs from "../../Breadcrumbs";
import NotificationItem from "../../Items/NotificationItem";

// Custom styles for MainNavbar
import { navbar, navbarContainer, navbarIconButton, navbarMobileMenu, navbarRow } from "./styles";

import {
  setMiniSidenav,
  setOpenConfigurator,
  setTransparentNavbar,
  useSoftUIController,
} from "../../../context";

// Images
import team2 from "../../../assets/images/team-2.jpg";
import logoSpotify from "../../../assets/images/small-logos/logo-spotify.svg";

/** Keeps navigation in the top bar and the page heading in the content below it. */
function MainNavbar({ absolute, light, isMini, customRoutes }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, showTimeDisplay } =
    controller;
  const [openMenu, setOpenMenu] = useState(false);
  const { pathname } = useLocation();
  const route = pathname.split("/").slice(1);
  const heading = pageHeadings[pathname];
  const title = customRoutes.length
    ? customRoutes[customRoutes.length - 1].label
    : route[route.length - 1];

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  // eslint-disable-next-line no-unused-vars
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  // Render the notifications menu
  // eslint-disable-next-line no-unused-vars
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        image={<img src={team2} alt="person" />}
        title={["New message", "from Laur"]}
        date="13 minutes ago"
        onClick={handleCloseMenu}
      />
      <NotificationItem
        image={<img src={logoSpotify} alt="person" />}
        title={["New album", "by Travis Scott"]}
        date="1 day"
        onClick={handleCloseMenu}
      />
      <NotificationItem
        color="secondary"
        image={
          <Icon fontSize="small" sx={{ color: ({ palette: { white } }) => white.main }}>
            payment
          </Icon>
        }
        title={["", "Payment successfully completed"]}
        date="2 days"
        onClick={handleCloseMenu}
      />
    </Menu>
  );

  return (
    <>
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
      >
        <Toolbar
          sx={(theme) => ({
            ...navbarContainer(theme),
            flexWrap: "wrap",
            ...(showTimeDisplay && {
              display: { xs: "flex", md: "grid" },
              gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
              "& > :last-child": { justifySelf: "end" },
            }),
          })}
        >
          <SoftBox color="inherit" sx={(theme) => navbarRow(theme, { isMini })}>
            {customRoutes.length === 0 && (
              <Breadcrumbs icon="home" title={title} route={route} light={light} />
            )}
            {customRoutes.length !== 0 && (
              <Breadcrumbs
                icon="home"
                title={title}
                route={customRoutes}
                light={light}
                customRoute={customRoutes}
              />
            )}
          </SoftBox>
          {showTimeDisplay && <TimeDisplay />}
          {isMini ? null : (
            <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
              {/* <SoftBox pr={1}>
              <SoftInput
                placeholder="Type here..."
                icon={{ component: "search", direction: "left" }}
              />
            </SoftBox> */}
              <SoftBox color={light ? "white" : "inherit"}>
                <IconButton
                  size="small"
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  <Icon className={light ? "text-white" : "text-dark"}>
                    {miniSidenav ? "menu_open" : "menu"}
                  </Icon>
                </IconButton>
                <IconButton
                  size="small"
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleConfiguratorOpen}
                  aria-label="Panel configuration"
                >
                  <Icon>settings</Icon>
                </IconButton>
                {/* <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Icon className={light ? "text-white" : "text-dark"}>notifications</Icon>
              </IconButton>
              {renderMenu()} */}
              </SoftBox>
            </SoftBox>
          )}
        </Toolbar>
      </AppBar>
      <SoftBox component="header" className="page-heading" sx={{ mb: { xs: 2.625, md: 3.25 } }}>
        <SoftTypography
          component="h1"
          fontWeight="regular"
          textTransform="uppercase"
          variant="h6"
          sx={({ typography }) => ({
            fontFamily: typography.displayFontFamily,
            fontSize: { xs: 32, md: 42 },
            lineHeight: 1.2,
            letterSpacing: "0.02em",
            overflowWrap: "anywhere",
            width: "fit-content",
            maxWidth: "100%",
          })}
          color={light ? "white" : "dark"}
        >
          {heading?.prefix}
          <SoftBox
            component="span"
            sx={({ palette }) => ({
              position: "relative",
              isolation: "isolate",
              whiteSpace: heading ? "nowrap" : "normal",
              "&::after": {
                content: '""',
                position: "absolute",
                zIndex: -1,
                inset: heading ? "auto -0.05em 0.08em -0.05em" : "auto -0.05em 0.08em 45%",
                height: "0.4em",
                backgroundColor: palette.brand.main,
                transform: "rotate(-1.1deg)",
                pointerEvents: "none",
              },
            })}
          >
            {heading?.accent || `${title.replace(/-/g, " ")}.`}
          </SoftBox>
        </SoftTypography>
        {heading?.description && (
          <SoftTypography
            component="p"
            variant="body2"
            color={light ? "white" : "text"}
            sx={{ mt: { xs: 1, md: 1.25 }, fontSize: 15, lineHeight: 1.6, maxWidth: "65ch" }}
          >
            {heading.description}
          </SoftTypography>
        )}
      </SoftBox>
    </>
  );
}

// Setting default values for the props of MainNavbar
MainNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  customRoutes: [],
};

// Typechecking props for the MainNavbar
MainNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  customRoutes: PropTypes.arrayOf(PropTypes.object),
};

export default MainNavbar;
